/* =====================================================================
   THE ROOM ENGINE — R8, THE MONTH RULERS, DRAWN LIVE

   The graduated border round the plate — the blocks, the month ticks,
   the month names, the day figures and the year at each January — used
   to be BAKED into the furniture texture (E9 §1, `#sheet-over`'s
   `sv-over-axes` group). A texture is a picture of ONE view, and this
   ruler graduates TIME: the record is one column per week, so a year is
   52.18 columns and the months walk against any fixed ruler. Panned to
   2024 the baked JAN sat nowhere near 2024's January.

   So it is drawn here instead, from the view, the way 8102 drew it
   (survey-map.js drawAxes :5460 — every rule below is that function's,
   with its line numbers). What is different: the paper is a PLANE IN A
   ROOM, not a screen-space overlay, so a graduation is a rectangle in
   SHEET UNITS lying on the paper at z = 0. That buys three things for
   nothing:

     - it holds its SIZE ON SCREEN under zoom, because the paper does:
       the zoom moves the ground under the window, not the sheet on the
       desk. At zoom 900 the blocks stretch (they measure the ground)
       and the month names do not grow by a hair (they are print);
     - at tilt it LIES ON THE PAPER and foreshortens with it, which is
       what a thing printed on paper does and what the baked one did;
     - it is under the index card, exactly as the baked one was
       (E9 §3's OPEN: the south ruler's DEC sits beneath the card's
       head) — the card is drawn after the desk, this before the ground.

   WHERE A GRADUATION GOES, under a bearing. A month is a MERIDIAN on
   the ground: a line of constant map x. The tick is the point where
   that meridian crosses the neat line, and at a bearing the two neat
   lines are crossed at two different places, so each is solved for its
   own line — `xAt()` below is engine.js `mapToSheet` inverted at h = 0.
   At bearing 0 it collapses to 8102's `toPx(alongOf(t))` exactly. Past
   about 89 degrees the meridian is parallel to the neat line and never
   crosses it: the tick is then DROPPED, which is the sheet's own rule
   for a graduation it cannot set (`A LABEL THAT WILL NOT FIT IS
   DROPPED, NOT CLIPPED`, survey-map.js:5611).

   ONE INSTANCED DRAW for the whole ruler — blocks, ticks and type
   together. A block is a solid instance (`iUV.x < 0`); a glyph is the
   same rectangle with a cut out of the lettering's own atlas, which is
   SHARED: the ruler adds its twenty-eight letters and ten figures to
   E3's charset (`RoomLettering.reserve`) and costs no second texture.
   ===================================================================== */
(function (root) {
  'use strict';
  var RU = root.RoomRuler = root.RoomRuler || {};

  var MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
                'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  /* every glyph the ruler can ever set: the twelve names and the figures */
  var CHARS = 'ABCDEFGJLMNOPRSTUVY0123456789';
  /* R30: the old flat pose has to come out of this file unchanged, and
     the band below is a change to it — the same 1.68:1 minification is
     there, it is just not the one Tommy is looking at. objects.js's own
     test, so the two cannot disagree about which pose is in force. */

  /* THE TYPE AND THE METAL, in SHEET UNITS. Every number is the v32
     overlay's own (styles.css .sv-ax-*), divided by the k E9's generator
     mapped that overlay onto the paper with — so the live ruler is set
     at exactly the size, weight and offset the baked one was printed at,
     and scales with the paper the same way (E9 §1.4). */
  var K32 = 0.7707275;                 // css px per sheet unit at the dump
  var P32 = 1 / K32;
  var SZ_MONTH = 13 * P32;             // .sv-ax-month  font-size: 13px
  var SZ_DAY = 10 * P32;               // .sv-ax-day    font-size: 10px
  var SZ_YEAR = 12 * P32;              // .sv-meridian-year font-size: 12px
  var TRK_MONTH = 0.08, TRK_DAY = 0.04, TRK_YEAR = 0.10;
  var HALO_MONTH = 1.5 * P32;          // half of the 3px paper stroke
  var HALO_DAY = 1.3 * P32;            // half of 2.6px
  var TICK_W = 0.9 * P32;              // .sv-ax-tick stroke-width
  var NAME_OFF = 5 * P32;              // the name is set east of its tick
  var BASE_OFF = 3 * P32;              // and clear of the block band by 3
  var YEAR_GAP = 5 * P32;              // and the year clear of the name
  var DAY_FIGURE_PX = 28;              // survey-map.js:5117
  var LABEL_MAX_WEEKS = 400;           // :5578 — nothing is legible past it
  var BLOCK_MAX_WEEKS = 600;           // :5560

  var INK = [0.18039216, 0.14901961, 0.10980392];   // --ink   #2e261c
  var INK2 = [0.33333333, 0.28235294, 0.22745098];  // --ink-2 #55483a
  var INK3 = [0.49019608, 0.42745098, 0.34901961];  // --ink-3 #7d6d59
  var SEPIA = [0.41568627, 0.30196078, 0.17254902]; // #6a4d2c, the year
  var BLOCK_A = 0.86, TICK_A = 0.7;                 // the two opacities

  var DAY_MS = 86400000;

  /* ==== R47: AND WHEN THE PAPER FOLDS, THE RULER FOLDS WITH IT =========
     X2 could not fold it, so the engine simply stopped drawing the ruler
     the moment the sheet had any fold in it: the month names, the day
     figures and the whole graduated border vanished at the first degree
     of the first turn. Tommy's item: the ruler folds with the panel it is
     printed on, because that is what print does.

     It is the SAME ARITHMETIC as the sheet's own — `RoomObjects.foldGLSL`
     is objects.js's FOLD_GLSL string, compiled here as well, so there is
     one copy of the three turns in the build and a graduation cannot
     drift off the paper it is set on.

     TWO THINGS THIS FILE HAS TO DECIDE THAT THE SHEET DOES NOT.

     A PANEL THAT HAS TURNED PAST VERTICAL IS SHOWING ITS BACK, and there
     is no print on the back of a sheet of paper. The sheet answers that
     per fragment with gl_FrontFacing; a ruler instance is a flat quad
     drawn with blending and no depth, so it answers it here instead and
     the whole instance is clipped away (`fn.z`, the panel's own normal).

     AND A GRADUATION IS IN FRONT OF THE MAP WHEN IT IS IN THE AIR. The
     ruler is drawn UNDER the ground, which is right for print lying on
     the desk (the ground paints the window over it) and wrong for print
     on a flap standing up between the eye and the window. So it is drawn
     in two passes while there is a fold: what is still flat before the
     ground, exactly as always, and what has left the desk after it.
     `uFoldPass` is nought when nothing is folded and the draw is one.
     ==================================================================== */
  /* ==== DR3, 2026-09-21: AND THE PAPER THE GRADUATIONS ARE PRINTED ON

     Tommy, with the south-east of the sheet over the journal: "paper
     changes the shape of the sheet, especially where it raises up over
     the journal, and therefore the bottom right-hand corner of the map
     isn't right ... if you look at the x-axis you can see those final
     months aren't quite positioned right. Basically, the map and axis
     labels go straight on but the paper bends up to the right."

     He is right, and the line above this file's own `z = 0` said why: it
     claimed "the paper is flat". It has not been flat since the drape
     went over the two books. Everything else that draws into the window
     goes through engine.js's `sheetToClip`, which since FD2 adds
     `paperSurf(p.xy)` to every point — the sheet's own print, the neat
     rule and the border dashes among it, rides the shoulder the journal
     puts under the sheet's south-east. The ruler did not, so DEC sat on
     the rule, OCT sat under it, and the last three months of the year
     drifted a measured 19.4 css px below the line they graduate.

     So the ruler compiles objects.js's own chunk too — one copy of the
     height field in the build, exactly as R47 took one copy of the fold —
     and `paperSurf` is the z the flat pass has always wanted. ==== */
  var PAP = (root.RoomObjects && root.RoomObjects.paperInkGLSL)
    ? root.RoomObjects.paperInkGLSL()
    /* the same stub engine.js:310 keeps, for a harness that loads this
       module without the desk under it: a flat sheet, and no board. */
    : 'float paperSurf(vec2 p){ return 0.0; }\nfloat boardH(vec2 p){ return 0.0; }\n';

  function VS_SRC() {
    var F = (root.RoomObjects && root.RoomObjects.foldGLSL) ||
      /* objects.js is loaded before this file and this branch has never
         been taken; it is here so a ruler can still be compiled by a
         harness that loads this module on its own. */
      'uniform vec4 uFoldC; uniform vec3 uFoldA; uniform float uFoldOn;\n' +
      'vec3 foldAt(vec3 q, vec2 g, inout vec3 n, out vec3 fn, out vec3 ft, out vec2 st){' +
      ' fn = vec3(0.0,0.0,1.0); ft = vec3(1.0,0.0,0.0); st = vec2(0.0); return q; }\n' +
      'float foldUp(vec2 p){ return 0.0; }\n';
    return `#version 300 es
precision highp float;
layout(location = 0) in vec2 a;
layout(location = 1) in vec4 iR;    // the graduation's rect, SHEET units
layout(location = 2) in vec4 iUV;   // its cut of the atlas; x < 0 is solid
layout(location = 3) in vec4 iC;    // ink rgb and alpha
layout(location = 4) in vec2 iH;    // the halo's radius, in atlas uv
out vec2 vUv; out vec4 vC; out vec4 vRect; out vec2 vHalo; out vec2 vA;
out vec2 vSheet;                    // LK1b: the station, for the lamp's pool
uniform vec3 uSheet; uniform mat4 uVP;
uniform float uFoldPass;            // R47: 0 all, 1 what is flat, 2 what is up
uniform float uFoldTop;             // R47: the highest leaf that has landed
/* DR3: the sheet's own surface, compiled ahead of the fold's chunk --
   the landed leaf's support below asks boardH, and GLSL wants it declared
   by then (objects.js SHEET_SRC puts the books ahead of FOLD_GLSL for the
   same reason). The two chunks share no name: the fold's are uFoldC,
   uFoldA and uFoldOn, and this one's all begin uPap or uOver. */
` + PAP + F + `
void main(){
  /* ==== RJ1 (2026-09-26): A BLOCK IS DRAWN HALF A PEN WIDER, AND ITS
     EDGES ARE COVERAGE. Tommy, of the slow lifeboat: "there's weird
     jitter in the edge underneath" — with the camera now moving evenly
     (0.93 map units a frame, measured on his Chrome), the blocks and
     ticks of the rulers were the one thing still stepping: a solid
     rectangle with no antialiasing (the context has none) puts each edge
     on whole pixels, so a block gliding 1.7 device px a frame jumped one
     pixel, then two. A block's quad now reaches one sheet unit (about two
     device px at the fit) past its rect, and the fragment shader shades
     each pixel by how much of it the true rect covers, so the edge moves
     by fractions of a pixel. Glyphs keep their exact quads. ==== */
  vec2 mg = iUV.x < 0.0 ? vec2(1.0) / max(iR.zw, vec2(1e-3)) : vec2(0.0);
  vec2 ae = a * (1.0 + 2.0 * mg) - mg;
  vA = ae;
  vec2 p = iR.xy + ae * iR.zw;
  vSheet = p;
  /* DR3: z = paperSurf(p): the ruler is PRINT, and print is on the face
     of the paper. It was z = 0 — the plane the paper lies ON — with the
     note below, which was true of the sheet E9 drew and has not been true
     since the drape went over the books. A graduation still needs no mesh
     of its own: it is the same SURFACE, sampled at its own four corners,
     which is what every other program on this paper does.

     R8's own note, kept whole, because it is still the reason there is no
     mesh here — only its first clause has changed:
     R47: and when the paper is NOT flat it is the same plane still — the
     panel's, turned. The instance's own centre decides which panel it is
     on, so a block cut at a crease in the layout below goes wholly with
     one side of it and nothing is sheared across a fold. */
  vUv = mix(iUV.xy, iUV.zw, a); vC = iC; vRect = iUV; vHalo = iH;
  if (uFoldOn <= 0.0) {
    /* R8's own line, kept whole: on a sheet that is lying flat this file
       emits exactly the instruction it has emitted since R8, so the fit
       is the same picture to the last bit and not merely to the eye. */
    /* DR3: sheetToClip's own line, to the letter — the height is in
       SHEET units and is scaled by uSheet.z with the other two.
       No depth bias: this draw has DEPTH_TEST off (draw(), below), so the
       z here does nothing but the perspective divide, which is precisely
       the thing that was putting NOV a centimetre off its rule. */
    gl_Position = uVP * vec4(uSheet.x + p.x*uSheet.z, -(uSheet.y + p.y*uSheet.z), paperSurf(p)*uSheet.z, 1.0);
    return;
  }
  vec3 n = vec3(0.0, 0.0, 1.0), fn, ft;
  vec2 g = iR.xy + iR.zw*0.5;
  float up = foldUp(g);
  if (uFoldPass > 0.5 && (uFoldPass > 1.5) != (up > 0.5)) {
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0); return;      // the other pass's
  }
  vec2 st; vec3 q = foldAt(vec3(p, 0.0), g, n, fn, ft, st);
  if (fn.z < 0.0) { gl_Position = vec4(0.0, 0.0, 2.0, 1.0); return; }   // face down
  /* AND SEVEN LEAVES OF A FOLDED MAP ARE UNDERNEATH. A graduation on a
     panel that has come to rest below the top of the pile is print with
     paper over it: the sheet sorts its own by the mesh's order and this
     is instanced with no depth buffer to appeal to, so it is asked
     directly. A panel still in the air (st.y) is over everything. */
  if (st.y < 0.5 && st.x < uFoldTop - uFoldC.w*0.45) {
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0); return;
  }
  /* ==== DR3: AND THE DESK THE LEAF HAS LANDED ON, which is objects.js
     sheetPos's own line (FOLD_SHEET_GLSL, the R65 (a) and DR1 (2) blocks)
     copied here without a figure changed. The sheet's print takes the
     board's height at the station the panel has come to rest over, scaled
     by how flat that panel is — one at rest, nought through the turn, one
     again once it has come down on the packet — so a month printed on the
     quarter that is lying on the field book rides the field book, and a
     month on a leaf standing in the air carries no book at all.

     foldAt gives this file the same fn sheetPos reads, so the two
     smoothsteps are the same number on the same frame; a graduation
     cannot part company with the print it is set among. ==== */
  q.z += boardH(q.xy)*smoothstep(0.94, 0.995, abs(fn.z));
  gl_Position = uVP * vec4(uSheet.x + q.x*uSheet.z, -(uSheet.y + q.y*uSheet.z), q.z*uSheet.z, 1.0);
}`;
  }

  /* the glyph half is E3's fragment shader (lettering.js LET_FS) — the
     same eight-tap dilated halo, so a month name on the margin is set
     with the same paper stroke as a name on the ground. */
  var FS = `#version 300 es
precision highp float; precision highp sampler2D;
in vec2 vUv; in vec4 vC; in vec4 vRect; in vec2 vHalo; in vec2 vA;
in vec2 vSheet;
out vec4 o;
uniform sampler2D uAtlas;
const vec3 PAPER = vec3(0.96862745,0.93725490,0.86666667);
/* ==== LK1b: THE MARGIN IS THE SAME PAPER UNDER THE SAME LAMP. The ruler
   carried no lantern at all, which was invisible while the lantern only
   warmed the paper; once LK1 let the night take the paper down outside
   the pool, the month names' paper haloes stood bright on it like stickers.
   The same lines and uniforms as the other copies (objects.js lampSheet);
   LB1: it is kept on the frame that strikes the print raster too, and the
   sheet does not light what it reads off the print again, so it is
   applied exactly once. */
uniform vec4 uLampS;
uniform float uLampN;
uniform float uLampOn;
vec3 lampTemp(vec3 c, vec2 wp){
  if (uLampN <= 0.0 || uLampOn <= 0.0) return c;
  vec2 d = (wp - uLampS.xy)/uLampS.zw;
  float pool = 1.0 - clamp((length(d) - 0.20)/0.80, 0.0, 1.0);
  vec3 t = mix(vec3(0.985,0.999,1.022), vec3(1.018,0.998,0.930), pool);
  float rr = length(d); float lit = 0.40 + 0.58*(1.0 - smoothstep(0.12, 0.95, rr));
  return c*mix(vec3(1.0), t*lit, uLampN);
}
float tap(vec2 uv){ return texture(uAtlas, clamp(uv, vRect.xy, vRect.zw)).a; }
void main(){
  if (vRect.x < 0.0) {                                           /* a block */
    /* RJ1: the share of this pixel inside the true rect, per axis */
    vec2 d = min(vA, 1.0 - vA) / max(fwidth(vA), vec2(1e-6));
    float cov = clamp(d.x + 0.5, 0.0, 1.0) * clamp(d.y + 0.5, 0.0, 1.0);
    if (cov < 0.003) discard;
    o = vec4(lampTemp(vC.rgb, vSheet)*vC.a*cov, vC.a*cov); return;
  }
  float f = tap(vUv);
  float hl = 0.0;
  if (vHalo.x > 0.0) {
    hl = max(max(max(tap(vUv + vec2( vHalo.x, 0.0)), tap(vUv + vec2(-vHalo.x, 0.0))),
                 max(tap(vUv + vec2(0.0,  vHalo.y)), tap(vUv + vec2(0.0, -vHalo.y)))),
             max(max(tap(vUv + vHalo*0.71), tap(vUv - vHalo*0.71)),
                 max(tap(vUv + vec2(vHalo.x, -vHalo.y)*0.71), tap(vUv + vec2(-vHalo.x, vHalo.y)*0.71))));
    hl = max(hl, f);
  }
  float al = max(f, hl*0.82) * vC.a;
  if (al < 0.003) discard;
  vec3 col = mix(PAPER, vC.rgb, clamp(f/max(max(f, hl*0.82), 1e-4), 0.0, 1.0));
  col = lampTemp(col, vSheet);
  o = vec4(col*al, al);
}`;

  function programs(H) { return H.prog(H.gl, VS_SRC(), FS, 'ruler'); }

  function attach(H) {
    var gl = H.gl, S = H.S, C = root.RoomField.C;
    var P = programs(H);
    var STRIDE = 14 * 4;
    var vao = gl.createVertexArray(), ibuf = gl.createBuffer(), qb = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, qb);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0,0, 1,0, 0,1, 1,1]), gl.STATIC_DRAW);
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, qb);
    gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, ibuf);
    [[1,4,0],[2,4,4],[3,4,8],[4,2,12]].forEach(function (d) {
      gl.enableVertexAttribArray(d[0]);
      gl.vertexAttribPointer(d[0], d[1], gl.FLOAT, false, STRIDE, d[2] * 4);
      gl.vertexAttribDivisor(d[0], 1);
    });
    gl.bindVertexArray(null);

    var buf = new Float32Array(4096), n = 0, o = 0, lastKey = '';
    var stats = { n: 0, blocks: 0, glyphs: 0, layoutMs: 0, months: 0,
                  atlas: '-', atlasPx: 0, epoch: 0, band: 0, bandMs: 0, drawnPx: 0 };
    var ownAtlas = null, ownTex = null, ownBand = 0, bandAt = 0;

    /* =================================================================
       R30: THE BAND THE RULER'S TYPE IS ACTUALLY SET AT

       Tommy, with the edge pose as the default: "the year text on the
       ruler is odd too, 2026 is hardly legible".

       It is not the foreshortening. Measured on the shipped pose at
       1536 x 864, devicePixelRatio 1.75: the paper at the HEAD band runs
       0.48693 css px to the sheet unit down the page against 0.55556
       across it, so the type there is 12 % short — squat, and right,
       because that is what print on a board pitched 22 degrees does.

       It is the ATLAS. A month name is SZ_MONTH = 16.87 sheet units, so
       at the head its em is 16.87 x 0.48693 x 1.75 = **14.37 device px**,
       and the year's is 13.27. The cut it was sampling is E3's, whose
       band is chosen by `bandFor()` for the PLATE's names — screen-space
       quads at 16 x dpr x cam.k — and lands on **32**. That is a 2.23:1
       MINIFICATION out of a LINEAR texture with no mipmaps: every other
       texel row of the glyph is thrown away, the thin strokes of a 2, a
       0 and a 6 go with them, and the paper halo (which is dilated in
       ATLAS texels, so it keeps its share of a glyph that has shrunk)
       washes what is left toward the paper. At the foot, where the pose
       foreshortens least, it is 1.99:1 and the same names read.

       R18 left this exact thing open: "if [the handover] is not to
       happen at all, the answer is to cut the ruler's own atlas at
       bandFor() rather than a flat 32." This is that, with the ruler's
       own `bandFor` — the size its type is drawn at ON THE PAPER, AT THE
       POSE, at the head, where the pitch foreshortens most. The head is
       the anchor and not the foot because a band too big minifies (which
       is what this fault is) and a band too small only magnifies, which
       is soft, not broken: at 1.75 the head asks for 16, and the foot's
       16.12 device px then comes out of a band-16 cut at 1.01:1.

       It keeps that cut. E3's is taken only when its band is the one the
       ruler wanted anyway, so there is at most one extra texture and the
       750 ms band change R18 measured does not happen at all. The cuts
       still key on `gen`, so nothing can draw from an atlas it did not
       lay out against.
       ================================================================= */
    var BANDS = [16, 24, 32, 48, 64, 96];          // lettering.js's ladder
    function drawnPx() {
      var L = S.layout, m = S.VP;
      if (!L) return 0;
      var PLy = C.py, tick = Math.max(3.5 / L.k, 7);
      var hy = PLy - C.gap - tick - BASE_OFF;      // the head's lettered line
      var hx = C.px + C.cw * C.weeks * 0.5;
      if (!m) return SZ_MONTH * L.k * S.dpr;       // before the first vp()
      function proj(x, y) {
        var wx = L.ox + x * L.k, wy = -(L.oy + y * L.k);
        var cx = m[0]*wx + m[4]*wy + m[12], cy = m[1]*wx + m[5]*wy + m[13],
            cw = m[3]*wx + m[7]*wy + m[15];
        if (cw <= 1e-4) return null;
        return [(cx/cw*0.5 + 0.5)*L.cw, (0.5 - cy/cw*0.5)*L.ch];
      }
      var o = proj(hx, hy), d = proj(hx, hy + 1);
      if (!o || !d) return SZ_MONTH * L.k * S.dpr;
      return SZ_MONTH * Math.hypot(d[0] - o[0], d[1] - o[1]) * S.dpr;
    }
    /* and it does not step on every frame of a tilt: the ladder is coarse
       and a re-cut is a raster, so the size is only asked again when it
       has moved 3 % from the size the standing band was chosen at */
    function wantBand() {
      var px = drawnPx();
      if (!px) return ownBand || 32;
      if (ownBand && Math.abs(px - bandAt) < bandAt * 0.03) return ownBand;
      bandAt = px;
      for (var i = 0; i < BANDS.length; i++) if (BANDS[i] >= px) return BANDS[i];
      return BANDS[BANDS.length - 1];
    }

    /* TWO ATLASES, IN ORDER. The ruler is FURNITURE and furniture is in
       frame 1, so it sets its type from its OWN 29-glyph cut the moment
       the face is in, and hands over to E3's shared one — into which its
       charset was reserved — the frame that one is rasterised, 750 ms
       later with the field. `?let=0` takes E3's away for good and the
       ruler keeps its own: a measuring switch does not cost it its type.

       R18 — THE RULER'S FIRST RENDER, AND WHY IT WAS WRONG. Tommy:
       "weird numbers all over the place on first render but fixes itself
       on zoom in and then back out". Two atlases are in play on an
       ordinary load and the ruler could not tell them apart.

       R8's comment above says the shared one is the only one on a page
       with the lettering on. It is not, and never was: `buildRuler()`
       runs in the SYNCHRONOUS boot and base.njk loads engine.js BEFORE
       lettering.js (both defer, in source order), so `RoomLettering` did
       not exist at attach and the flag that was supposed to mean "wait
       for E3" latched false on every load. The ruler has therefore always
       cut its own band-32 atlas as soon as the face was in — which is why
       the month names ARE in frame 1 (room-rev2/R12-after-f1-230.png),
       750 ms before E3's, and that is the picture that shipped and the
       one this item keeps.

       What was wrong is that the layout's relayout key carried the
       atlas's BAND. A band is not an identity: `bandFor()` is the
       smallest of [16,24,32,48,64,96] at or above 16·dpr·k, so at
       devicePixelRatio 2 — or 1.875, a 1.25 screen at 150% browser zoom —
       the plate's atlas is cut at 32 as well. Same number, different
       packing: the key never changed, the cached cuts were never redone,
       and the draw bound E3's texture with THIS atlas's rectangles. Every
       month name came out of the wrong part of the sheet — stray figures
       and dots. A pan or a zoom changed the key by the view, and it
       healed itself. At 1.25 the plate's band is 24, the key did change,
       and that is why the cold harness never saw it.

       So: key on the cut's own `gen`, resolve the atlas ONCE per draw so
       the layout and the bind cannot disagree, and when E3's does arrive
       THROW THIS ONE AWAY — nothing must be able to draw from the cuts of
       an atlas that is no longer there. */
    function atlas() {
      var LT = S.letters, A = LT && LT.atlas && LT.atlas();
      var want = wantBand();
      /* E3's cut, but only when it is the size this type is set at. R18
         took it whatever its band was, which is how a 14.4 px em came to
         be sampled out of a 32 px raster. */
      if (A && A.band === want) {
        if (ownTex) {            /* superseded by a cut of the right size */
          gl.deleteTexture(ownTex); ownTex = null; ownAtlas = null; ownBand = 0;
        }
        return { A: A, tex: LT.tex(), id: 'L' + A.gen };
      }
      if (!let_() || !S.fontsIn) {
        /* nothing of our own can be cut yet: E3's is better than no type */
        return A ? { A: A, tex: LT.tex(), id: 'L' + A.gen } : null;
      }
      if (!ownAtlas || ownBand !== want) {
        /* twenty-nine roman glyphs and NO italic: `i: ''` used to read as
           "not given" and cut the whole of ASCII in italic beside them —
           149 glyphs on a 741 x 744 canvas, rasterised and uploaded
           INSIDE frame 1. */
        if (ownTex) gl.deleteTexture(ownTex);
        ownAtlas = LET.buildAtlas(want, { r: CHARS, i: '' });
        ownBand = want;
        ownTex = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, ownTex);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, ownAtlas.cv);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        stats.band = want; stats.bandMs = ownAtlas.ms; stats.drawnPx = +bandAt.toFixed(2);
      }
      return { A: ownAtlas, tex: ownTex, id: 'O' + ownAtlas.gen };
    }

    function push(x, y, w, h, u0, v0, u1, v1, c, al, hx, hy) {
      if (o + 14 > buf.length) {
        var b2 = new Float32Array(buf.length * 2); b2.set(buf); buf = b2;
      }
      buf[o] = x; buf[o+1] = y; buf[o+2] = w; buf[o+3] = h;
      buf[o+4] = u0; buf[o+5] = v0; buf[o+6] = u1; buf[o+7] = v1;
      buf[o+8] = c[0]; buf[o+9] = c[1]; buf[o+10] = c[2]; buf[o+11] = al;
      buf[o+12] = hx; buf[o+13] = hy;
      o += 14; n++;
    }
    /* R47: AND A BLOCK IS CUT AT THE CREASES IT CROSSES. A month's block
       is a week or more of ground — up to a couple of hundred sheet units
       — and the fold turns whole panels, so a block that straddles a
       crease has to become two blocks or it is sheared across the fold.
       The cut is made HERE, in the layout, and not in the shader, because
       the creases never move: it is the same four lines at every zoom, at
       every bearing and at every stage of the fold, so it costs one pass
       over four numbers at layout time and nothing per frame. (Glyphs
       are not cut: a letter is an atlas
       rectangle and cannot be halved. A name is 17 units wide against a
       591-unit panel, so it goes whole, with its own centre.) */
    function block(x0, y0, x1, y1, col, al) {
      if (x1 - x0 <= 0.2 || y1 - y0 <= 0.2) return;
      var cr = creases();
      if (cr) {
        for (var i = 0; i < cr.x.length; i++) {
          var c = cr.x[i];
          if (c > x0 + 0.2 && c < x1 - 0.2) { block(x0, y0, c, y1, col, al); block(c, y0, x1, y1, col, al); return; }
        }
        if (cr.y > y0 + 0.2 && cr.y < y1 - 0.2) {
          block(x0, y0, x1, cr.y, col, al); block(x0, cr.y, x1, y1, col, al); return;
        }
      }
      push(x0, y0, x1 - x0, y1 - y0, -1, 0, 0, 0, col, al, 0, 0);
      stats.blocks++;
    }
    /* the four lines the paper turns about, in sheet units: the middle
       crease, the quarter crease, the three-quarter one (which is the
       quarter crease's own place once the east half has come over — the
       gate is the position as it stands) and the crease across */
    function creases() {
      var O = root.RoomObjects;
      if (!O || !O.foldOn || !O.foldGeom || !O.foldT) return null;
      var g = O.foldGeom();
      return { x: [g.c2, g.c1, 2 * g.c1 - g.c2], y: g.cy };
    }

    /* E3 IS RESOLVED LATE, AND THE RULER'S GLYPHS ARE RESERVED THEN.
       This module is defined BEFORE lettering.js — it has to be: the
       engine boots between the two and the ruler is print, so it is in
       frame 1 — so `RoomLettering` does not exist yet at attach. It is
       picked up on the first layout instead, which is still long before
       the atlas is cut (that waits for the field), and the ruler's
       twenty-nine glyphs go into the charset in the same breath. */
    var LET = null;
    function let_() {
      if (!LET && root.RoomLettering) {
        LET = root.RoomLettering;
        if (LET.reserve) LET.reserve(CHARS);
      }
      return LET;
    }
    function inkWidth(t, size, track) { return LET.inkWidth(t, size, false, track); }

    /* ---------------------------------------------------------------
       THE LAYOUT. Everything below is in SHEET UNITS on the paper.
       --------------------------------------------------------------- */
    function layout(force, at) {
      var L = S.layout, v = S.view, f = S.fld;
      /* the reserve has to happen on the FIRST layout, whatever else it
         decides, or the plate's atlas is cut without the ruler's glyphs */
      let_();
      /* R18: the epoch is a KEY, not a constant. Frame 1 reads the home
         year out of the record's text (engine.js, "THE EPOCH BEFORE THE
         RECORD"); the record's own epoch lands with the field. They agree
         today — but a ruler that graduates time and does not relayout
         when time moves under it is the same fault as the atlas one. */
      var epoch = (f.days && f.days.epochMs) || f.epochMs;
      if (!epoch || !L || !S.portal) { n = 0; return false; }
      if (at === undefined) at = atlas();
      /* R47: and whether the blocks are cut at the creases. It is one bit
         and it is not the fold's PROGRESS: the creases never move, so the
         cut layout is made once when the sheet starts to fold and once
         when it comes flat again, and not sixty times a second through
         the gesture. Flat, nothing is cut and this file lays exactly the
         instances it laid before (the fit is diff 0). */
      var rul = S.ruling || null;
      var key = [v.x.toFixed(3), v.w.toFixed(3), S.tilt.toFixed(4), S.yaw.toFixed(4),
                 L.k.toFixed(4), at ? at.id : '-', epoch, S.liftS || 0,
                 creases() ? 'f' : '-', rul ? rul.p.toFixed(4) + ',' + rul.a.toFixed(3) : '-'].join('|');
      if (!force && key === lastKey) return false;
      lastKey = key;
      stats.atlas = at ? at.id : '-';
      stats.atlasPx = at ? at.A.W * at.A.H * 4 : 0;
      stats.epoch = epoch;
      var t0 = performance.now();
      n = 0; o = 0; stats.blocks = 0; stats.glyphs = 0; stats.months = 0;

      var A = at && at.A;
      var kk = S.portal.w / v.w / L.k;                 // sheet units per map unit
      var mx0 = (S.portal.l - L.ox) / L.k + S.portal.w / L.k * 0.5;
      var my0 = (S.portal.t - L.oy) / L.k + S.portal.h / L.k * 0.5;
      var cx = v.x + v.w / 2, cv = v.y + v.h / 2;
      var ct = Math.cos(S.tilt) || 1e-6;
      var cy = Math.cos(S.yaw), sy = Math.sin(S.yaw);
      var lift = S.liftS || 0;
      /* the neat lines, on the PAPER (the constants the frame is printed
         from), and the bands outside them */
      var PLx = C.px, PLy = C.py, PLw = C.cw * C.weeks, PLh = C.ch * (7 + C.EDGE_ROWS * 2);
      var gap = C.gap;                                 // :5512
      var tick = Math.max(3.5 / L.k, 7);               // :5511, its css floor
      var nBlk0 = PLy - gap - tick, nBlk1 = PLy - gap;
      var sBlk0 = PLy + PLh + gap, sBlk1 = PLy + PLh + gap + tick;
      var headY = nBlk0 - BASE_OFF;                    // the two lettered lines
      var footY = sBlk1 + 12 * P32;                    // :5513

      /* the inverse of engine.js mapToSheet at h = 0: where the meridian
         through map x `mx` cuts the paper line y = Y */
      function xAt(mx, Y) {
        if (Math.abs(cy) < 1e-3) return null;
        var dx = (mx - cx) * kk;
        var r1 = (Y - my0 - lift) / ct;
        var dy = (r1 - dx * sy) / cy;
        return mx0 + dx * cy - dy * sy;
      }
      /* and where the parallel through map y `my` cuts the paper line x = X */
      function yAt(my, X) {
        if (Math.abs(cy) < 1e-3) return null;
        var dy = (my - cv) * kk;
        var dx = ((X - mx0) + dy * sy) / cy;
        return my0 + (dx * sy + dy * cy) * ct + lift;
      }
      function alongOf(t) { return C.px + t * C.cw; }
      function acrossOf(u) { return C.py + (u + C.EDGE_ROWS) * C.ch; }
      function dateAt(t) { return new Date(epoch + t * 7 * DAY_MS); }
      function tOfMs(ms) { return (ms - epoch) / (7 * DAY_MS); }

      /* THE SPAN OF TIME THE NEAT LINES ACTUALLY CROSS. At bearing 0 it
         is the view; under a bearing the top edge of the window reaches
         further along time than the middle does, by the skew of the
         plate's own depth. */
      var skew = Math.abs(sy / (cy || 1e-6)) * (PLh / (kk * ct)) + v.w * 0.02;
      var tFrom = Math.floor((v.x - skew - C.px) / C.cw) - 1;
      var tTo = Math.ceil((v.x + v.w + skew - C.px) / C.cw) + 1;
      var span = tTo - tFrom;

      /* ---- the graduated border (:5519) -------------------------------
         Along the time axis a block is a WEEK; across it a block is a
         DAY — seven of them, in four pairs. The blocks MEASURE THE
         GROUND, so they stretch with the zoom: that is the whole point
         of them, and the only part of the ruler that is allowed to. */
      if (span < BLOCK_MAX_WEEKS) {
        for (var w = tFrom; w <= tTo; w++) {
          if (w & 1) continue;
          var ax0 = xAt(alongOf(w), nBlk1), ax1 = xAt(alongOf(w + 1), nBlk1);
          if (ax0 !== null) block(Math.max(Math.min(ax0, ax1), PLx), nBlk0,
                                  Math.min(Math.max(ax0, ax1), PLx + PLw), nBlk1, INK, BLOCK_A);
          var bx0 = xAt(alongOf(w), sBlk0), bx1 = xAt(alongOf(w + 1), sBlk0);
          if (bx0 !== null) block(Math.max(Math.min(bx0, bx1), PLx), sBlk0,
                                  Math.min(Math.max(bx0, bx1), PLx + PLw), sBlk1, INK, BLOCK_A);
        }
      }
      [[0,1],[2,3],[4,5],[6,7]].forEach(function (r) {
        var wy0 = yAt(acrossOf(r[0]), PLx), wy1 = yAt(acrossOf(r[1]), PLx);
        if (wy0 !== null) block(PLx - gap - tick, Math.max(Math.min(wy0, wy1), PLy),
                                PLx - gap, Math.min(Math.max(wy0, wy1), PLy + PLh), INK, BLOCK_A);
        var ey0 = yAt(acrossOf(r[0]), PLx + PLw), ey1 = yAt(acrossOf(r[1]), PLx + PLw);
        if (ey0 !== null) block(PLx + PLw + gap, Math.max(Math.min(ey0, ey1), PLy),
                                PLx + PLw + gap + tick, Math.min(Math.max(ey0, ey1), PLy + PLh), INK, BLOCK_A);
      });

      /* ==== LD6 (2026-10-02): THE EMPTY BOX, RULED WHILE IT WAITS =========
         The loading investigation (L2): on a slow compile the plate stood
         as an empty dashed box for 1-1.5 s, then names on bare paper. While
         the ground's programs are still in the driver's hands (engine.js
         startRuling sets S.ruling, and only then) the box is ruled: a hair
         for every week and a heavier one for every month, inked west to
         east, each line drawn down the plate as the front passes it. They
         are the record's own columns (alongOf), so the land that comes in
         over them comes in on the lines that were ruled for it, and they
         go (S.ruling.a) as the land is drawn over them. At a bearing the
         columns are not vertical and nothing is ruled. ==== */
      if (rul && rul.a > 0 && Math.abs(sy) < 1e-3) {
        var front = PLx + PLw * rul.p, run = PLw * 0.06;
        var rule = function (t, wHalf, al) {
          var x = xAt(alongOf(t), PLy);
          if (x === null || x < PLx + 0.5 || x > PLx + PLw - 0.5 || x > front) return;
          var len = Math.min(1, (front - x) / run) * PLh;
          block(x - wHalf, PLy, x + wHalf, PLy + len, INK, al * rul.a);
        };
        for (var rw = Math.max(tFrom, 0); rw <= tTo; rw++) rule(rw, TICK_W * 0.28, 0.10);
        var rm = new Date(Date.UTC(dateAt(tFrom).getUTCFullYear(), dateAt(tFrom).getUTCMonth(), 1));
        for (var rg = 0; rg < 200 && rm <= dateAt(tTo + 1); rg++) {
          rule(tOfMs(rm.getTime()), TICK_W * 0.45, 0.26);
          rm = new Date(Date.UTC(rm.getUTCFullYear(), rm.getUTCMonth() + 1, 1));
        }
      }

      /* ---- the graduations themselves ---------------------------- */
      if (span > LABEL_MAX_WEEKS || !A || !LET) { flush(t0); return true; }

      var scale = 0, qw = 0, qh = 0;
      function setType(size) {
        scale = size / A.band; qw = A.cw * scale; qh = A.ch * scale;
      }
      /* one word, set from its pen on one of the two lettered lines */
      function word(text, penX, baseY, size, track, col, halo) {
        setType(size);
        var ox = -A.originX * scale, oy = -A.originY * scale;
        var hx = halo > 0 ? (halo * A.band / size) / A.W : 0;
        var hy = halo > 0 ? (halo * A.band / size) / A.H : 0;
        var x = penX;
        for (var i = 0; i < text.length; i++) {
          var r = A.rects['r' + text[i]];
          if (r && text[i] !== ' ') {
            push(x + ox, baseY + oy, qw, qh, r[0], r[1], r[2], r[3], col, 1, hx, hy);
            stats.glyphs++;
          }
          x += LET.advOf(text[i], false) * size + track * size;
        }
      }

      /* THE MONTHS. One entry per first-of-the-month in the span, at its
         TRUE column: the record is one column per week, so a month's
         first day is at t = (its midnight - the epoch) / a week, and
         nothing about that is a whole number of columns. */
      var d0 = dateAt(tFrom), d1 = dateAt(tTo + 1);
      var months = [];
      var mm = new Date(Date.UTC(d0.getUTCFullYear(), d0.getUTCMonth(), 1));
      for (var guard = 0; guard < 500 && mm <= d1; guard++) {
        months.push({ t: tOfMs(mm.getTime()), m: mm.getUTCMonth(), y: mm.getUTCFullYear() });
        mm = new Date(Date.UTC(mm.getUTCFullYear(), mm.getUTCMonth() + 1, 1));
      }

      /* the day figures join the months once a day is wide enough to
         carry one, and then EVERY day carries one (:5104) */
      var dayPx = (C.cw / 7) * ((S.portalScr || S.portal).w / v.w);   /* MP2: on the glass */
      var figures = [];
      if (dayPx >= DAY_FIGURE_PX && span < 60) {
        var westPad = (inkWidth('30', SZ_DAY, TRK_DAY) / 2 + 4 * P32) / C.cw;
        for (var dt = Math.floor(tFrom * 7); dt <= (tTo + 1) * 7; dt++) {
          var dd = new Date(epoch + dt * DAY_MS);
          var tt = dt / 7 + 0.5 / 7;
          var clear = true;
          for (var q = 0; q < months.length; q++) {
            var claim = (NAME_OFF + inkWidth(MONTHS[months[q].m], SZ_MONTH, TRK_MONTH) + 8 * P32) / C.cw;
            if (tt > months[q].t - westPad && tt < months[q].t + claim) { clear = false; break; }
          }
          if (clear) figures.push({ t: tt, dom: dd.getUTCDate() });
        }
      }

      months.forEach(function (M) {
        var txt = MONTHS[M.m], wpx = inkWidth(txt, SZ_MONTH, TRK_MONTH);
        var yr = String(M.y), ywpx = inkWidth(yr, SZ_YEAR, TRK_YEAR);
        stats.months++;
        [[headY, nBlk1, -C.band], [footY, sBlk0, C.band]].forEach(function (line) {
          var px = xAt(alongOf(M.t), line[1]);
          if (px === null) return;
          /* nothing is graduated outside the neat line, and a word that
             will not fit whole is not set at all (:5605) */
          if (px < PLx + 1 || px + NAME_OFF + wpx > PLx + PLw - 1) return;
          /* the tick: from the neat line outward, across the block band */
          var ty = line[2] < 0 ? PLy + line[2] : PLy + PLh;
          block(px - TICK_W / 2, ty, px + TICK_W / 2, ty + Math.abs(line[2]), INK, TICK_A);
          word(txt, px + NAME_OFF, line[0], SZ_MONTH, TRK_MONTH, INK2, HALO_MONTH);
          /* THE YEAR AT ITS OWN JANUARY. The month name NEVER changes
             its text (:5581) — a label that grew by seventy pixels when
             you panned one week west is what that rule is against — so
             the year is a SECOND graduation, in the meridian's own
             sepia, added and dropped on its own. */
          if (M.m === 0 && px + NAME_OFF + wpx + YEAR_GAP + ywpx < PLx + PLw - 1)
            word(yr, px + NAME_OFF + wpx + YEAR_GAP, line[0], SZ_YEAR, TRK_YEAR, SEPIA, HALO_MONTH);
        });
      });

      figures.forEach(function (D) {
        var txt = String(D.dom), wpx = inkWidth(txt, SZ_DAY, TRK_DAY);
        [[headY, nBlk1], [footY, sBlk0]].forEach(function (line) {
          var px = xAt(alongOf(D.t), line[1]);
          if (px === null) return;
          if (px - wpx / 2 < PLx + 1 || px + wpx / 2 > PLx + PLw - 1) return;
          word(txt, px - wpx / 2, line[0], SZ_DAY, TRK_DAY, INK3, HALO_DAY);
        });
      });

      flush(t0);
      return true;
    }

    function flush(t0) {
      gl.bindBuffer(gl.ARRAY_BUFFER, ibuf);
      gl.bufferData(gl.ARRAY_BUFFER, buf.subarray(0, o), gl.DYNAMIC_DRAW);
      stats.n = n;
      stats.layoutMs = +(performance.now() - t0).toFixed(3);
    }

    function draw(pass) {
      /* THE ATLAS IS RESOLVED ONCE, and the same one both lays the cuts
         out and is bound to sample them (R18): asking twice is what let a
         layout made against one atlas be drawn against another. */
      var at = atlas();
      layout(false, at);
      if (!n) return;
      /* the program is compiled at boot (engine.js buildRuler says why),
         but `?prog=` experiments and a lost context can both leave it
         unlinked: the ruler is furniture, not a reason to throw. */
      if (!P.u) { if (H.finish) H.finish(P); if (!P.u) return; }
      gl.useProgram(P);
      gl.bindVertexArray(vao);
      gl.activeTexture(gl.TEXTURE3);
      gl.bindTexture(gl.TEXTURE_2D, at ? at.tex : null);
      gl.uniform1i(P.u.uAtlas, 3);
      gl.uniformMatrix4fv(P.u.uVP, false, S.VP);
      gl.uniform3f(P.u.uSheet, S.layout.ox, S.layout.oy, S.layout.k);
      /* R47: the fold, from the sheet's own geometry — written every
         frame whether or not anything is folded, for objects.js's reason:
         a uniform that is only sometimes written holds whatever the last
         caller left in it. `uFoldPass` is 0 when the sheet is flat and
         the ruler is one draw, exactly as it has always been. */
      var O = root.RoomObjects, fg = (O && O.foldGeom) ? O.foldGeom() : null;
      gl.uniform4f(P.u.uFoldC, fg ? fg.c1 : 0, fg ? fg.c2 : 0, fg ? fg.cy : 0, fg ? fg.leaf : 0);
      gl.uniform3f(P.u.uFoldA, fg ? fg.a[0] : 0, fg ? fg.a[1] : 0, fg ? fg.a[2] : 0);
      gl.uniform1f(P.u.uFoldOn, (fg && fg.on) ? fg.t : 0);
      gl.uniform1f(P.u.uFoldPass, pass || 0);
      gl.uniform1f(P.u.uFoldTop, (fg && fg.on && O.foldTop) ? O.foldTop() : 0);
      /* DR3: and the height field behind paperSurf — the drape's boxes,
         the lie and the crease — written from the SAME list the sheet's
         own two programs are written from (objects.js paperInkUniforms,
         engine.js:2593 makes the same call for the ground). Written every
         frame, for the reason the fold's are: a uniform that is only
         sometimes written holds whatever the last caller left in it. It
         is a no-op on a build without the desk, where uPapRect is not an
         active uniform and the call returns at its first line. */
      if (O && O.paperInkUniforms) O.paperInkUniforms(gl, P, S);
      /* LK1b: the one lamp, and off on the print raster's frame */
      if (P.u.uLampS) {
        var lsh = (O && O.lampSheet) ? O.lampSheet(S) : [0, 0, 1, 1, 0];
        gl.uniform4f(P.u.uLampS, lsh[0], lsh[1], lsh[2], lsh[3]);
        gl.uniform1f(P.u.uLampN, lsh[4]);
        gl.uniform1f(P.u.uLampOn, 1);   /* LB1: the lantern is in the print */
      }
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.disable(gl.DEPTH_TEST);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, n);
      gl.disable(gl.BLEND);
      gl.bindVertexArray(null);
    }

    return { layout: layout, draw: draw,
             invalidate: function () { lastKey = ''; },
             stats: function () { return stats; } };
  }

  RU.attach = attach;
  RU.precompile = programs;
  RU.MONTHS = MONTHS;
  RU.CHARS = CHARS;
})(typeof self !== 'undefined' ? self : this);
